<script lang="ts">
	import { querystring } from 'svelte-spa-router';
	//Stores
	import { home_data, user_data, user_settings } from 'src/stores/account/user';
	//Services
	import { postReq } from '../../../services/http';
	import { format } from '../../../services/i18n';
	//Design system components
	import {
		Button,
		Alert,
		Text,
		toast,
		Input,
		Icon,
		Container,
		IconButton,
	} from '@Shelly/shelly-design-system';
	import { delete_home, save_home } from 'src/helpers/home_helpers';
	import { isMultiHome, getInstallerCodes, removeInstallerCode } from 'src/helpers/installer_helpers';
	import RevokeConfirmationModal from './RevokeConfirmationModal.svelte';
	import { onMount } from 'svelte';
	import CountdownTimer from './CountdownTimer.svelte';
	import { timestampToDate } from 'src/helpers/date_helper';
	import GrantInstallerInfoModal from './GrantInstallerInfoModal.svelte';
	import GrantInstallerAccessModal from './GrantInstallerAccessModal.svelte';

	$: userId = $user_data?.user_id;
	$: multiHome = isMultiHome($user_data);
	$: installerCodes = getInstallerCodes($user_settings?.icodes, userId) as string[];

	let installerCode = '';
	let installerData: { [key: string]: string } = {};
	let revokeReqLoading = false;
	let revokeShowModal = false;
	let addReqLoading = false;
	let showInfoModal = false;
	let showModal = false;
	let codePopover = '';
	let revokedCode = '';
	let message = '';

	// re-grant access variables
	let regrantAccessCode = '';

	onMount(() => {
		const searchParams = new URLSearchParams($querystring);
		const code = searchParams.get('code');
		if (searchParams.has('installerAccess')) {
			installerCode = code;
			getInstallersData();
		}
	});

	const getInstallersData = async (manual?: boolean) => {
		try {
			const res = await postReq('user/get_installer_info', {
				icode: installerCode,
				env: import.meta.env.VITE_APP_BUILD,
			});

			if (res.isok) {
				installerData = res.data as any;
				const realInstallerCode = installerData?.electrician_code || installerData.code;
				if (
					installerCodes.includes(realInstallerCode) ||
					$home_data?.icodes?.[installerData.code]?.electrician_code ===
						installerData?.electrician_code
				) {
					toast.open({
						type: 'info',
						title: $format('toast_used_installer_code', {
							default: 'You have already used this installer code',
						}),
					});
					if (!manual) installerCode = '';
					return;
				}
				showModal = true;
			} else {
				toast.open({
					type: 'danger',
					title: $format('toast_wrong_installer_code', {
						default: 'Wrong installer code',
					}),
				});
				if (!manual) installerCode = '';
			}
		} catch (err) {
			console.log('Error on the request (Network error) ', err);
			toast.open({ type: 'danger', title: $format('error_global') });
			if (!manual) installerCode = '';
		}
	};

	const revokeAccess = async (code) => {
		revokeReqLoading = true;
		const res = await postReq('user/remove_installer', {
			icode: code,
			env: import.meta.env.VITE_APP_BUILD,
			...(message.trim() && { message }),
		});

		if (res.isok) {
			const { icodes: nextIcodes, userCodes: remainingUserCodes } = removeInstallerCode(
				$user_settings.icodes,
				code,
				multiHome,
				userId,
			);
			user_settings.set({ ...$user_settings, icodes: nextIcodes });
			if (remainingUserCodes.length <= 0) {
				delete_home('icodes');
			} else {
				const newHomeDataIcodes = $home_data.icodes;
				delete newHomeDataIcodes[code];
				save_home('icodes', newHomeDataIcodes);
			}

			if (Object.keys($home_data?.installers_time).length > 1) {
				const newHomeDataInstallersTime = $home_data?.installers_time;
				delete newHomeDataInstallersTime[code];
				save_home('installers_time', newHomeDataInstallersTime);
			} else {
				delete_home('installers_time');
			}
		} else {
			toast.open({
				type: 'danger',
				title: $format('toast_wrong_installer_code', {
					default: 'Wrong installer code',
				}),
			});
		}

		message = '';
		revokedCode = '';
		revokeShowModal = false;
		revokeReqLoading = false;
	};
</script>

<Alert
	class="mb-s"
	heading={$format('alert_info_installer_access', {
		default:
			'Granting access to an installer allows them to work on your home. During this period, you can manually grant access again if needed. You can revoke access at any time.',
	})}
/>

<Text styleType="body-small" class="mb-s text-600">
	{$format('text_installer_code_description', {
		default: 'Enter the provided installer code:',
	})}
</Text>

<Input
	label="XXXXXXXX"
	class="mb-s mb-md-0 width-full width-md-auto"
	icon="fa-solid fa-key"
	bind:value={installerCode}
	id="input-fzd8o9k"
	aria-labelledby="input-fzd8o9k"
/>

<Button
	size="m"
	class={`${installerCodes.length > 0 ? 'mb-m' : ''} width-full width-md-auto`}
	disabled={!installerCode || addReqLoading}
	on:click={() => getInstallersData(true)}
>
	{$format('button_grant_access', { default: 'Grant access' })}
</Button>

{#if $home_data?.icodes && installerCodes.length > 0}
	<hr class="mb-m" />
	<Text styleType="body-small" class="mb-s text-600">
		{$format('text_installer_code_grant_access', {
			default: 'Installer codes with granted access:',
		})}
	</Text>
	{#each installerCodes as code}
		<div class="code-container">
			<Container padding="xs" class="pt-xs pb-xs">
				<div class="d-flex align-items-center justify-content-between flex-wrap">
					<div class="mb-s d-flex align-items-center">
						<Icon icon="fas fa-user-helmet-safety" size="s" />
						<Text styleType="body-small" class="ml-s text-600">
							{($home_data?.icodes[code]?.electrician_name &&
								`${$home_data?.icodes[code]?.electrician_name} (${$home_data?.icodes[code]?.company_name})`) ||
								$home_data?.icodes[code]?.name ||
								code}
						</Text>
					</div>
					<div class="mb-s d-flex align-items-center">
						<IconButton
							aria-label={$format('button_re_grant_access', {
								default: 'Re-grant access',
							})}
							class="mr-xs"
							icon="fa-solid fa-arrows-rotate"
							on:click={() => {
								installerData = $home_data.icodes[code];
								regrantAccessCode = code;
								showModal = true;
							}}
						/>
						<IconButton
							aria-label={$format('button_info', { default: 'Info' })}
							variant="help"
							icon="fa-solid fa-circle-info"
							on:click={() => {
								codePopover = code;
								showInfoModal = true;
							}}
						/>
					</div>
				</div>
				{#if $home_data?.installers_time}
					{#if $home_data?.installers_time[code] > new Date().getTime()}
						<Alert
							heading={$format('info_installer_access_granted', {
								default: 'Installer access granted until {time}',
								values: {
									time: timestampToDate($home_data?.installers_time[code]),
								},
							})}
						/>
					{:else}
						<Alert
							type="warning"
							heading={$format('heading_warning_installer_access_expired', {
								default: 'Installer access has expired',
							})}
							message={$format('message_warning_installer_access_expired', {
								default: 'You can either grant access again or revoke it.',
							})}
						/>
					{/if}
				{/if}
				<div class="d-flex align-items-center flex-wrap actions-row">
					{#if $home_data?.installers_time && $home_data?.installers_time[code] <= new Date().getTime()}
						<Button
							size="m"
							class="mr-s mt-s"
							on:click={() => {
								installerData = $home_data.icodes[code];
								regrantAccessCode = code;
								showModal = true;
							}}
						>
							{$format('button_re_grant_access', {
								default: 'Grant access again',
							})}
						</Button>
					{/if}
					<Button
						size="m"
						class="mr-s mt-s"
						variant="primary"
						disabled={revokeReqLoading}
						on:click={() => {
							revokedCode = code;
							revokeShowModal = true;
						}}
					>
						{$format('button_revoke_access', {
							default: 'Revoke access',
						})}
					</Button>
					{#if $home_data?.installers_time && $home_data?.installers_time[code] > new Date().getTime()}
						<div class="countdown-wrapper mt-s">
							<CountdownTimer targetTime={$home_data?.installers_time[code]} />
						</div>
					{/if}
				</div>
			</Container>
		</div>
	{/each}
{/if}

<GrantInstallerAccessModal
	bind:message
	bind:showModal
	bind:addReqLoading
	bind:installerData
	bind:installerCode
	bind:regrantAccessCode
/>

<GrantInstallerInfoModal bind:showInfoModal bind:codePopover />

<!-- revoke access modal -->
<!-- @ToDo - reword that we are revoking access and removing the installer -->
<RevokeConfirmationModal
	bind:message
	bind:revokeReqLoading
	bind:showModal={revokeShowModal}
	revokeInstallerAccess
	on:revoke={() => revokeAccess(revokedCode)}
	mainText={$format('message_modal_revoke_installers_access', {
		default:
			'Revoking the installer’s access will immediately remove their permissions and prevent them from accessing your home.',
	})}
/>

<!-- @ToDo - add a option to just remove the access will need API @spas and @tomov -->

<style>
	.code-container {
		margin-bottom: 8px;
	}

	.code-container:last-child {
		margin-bottom: 0;
	}

	.actions-row {
		min-width: 0;
	}

	.countdown-wrapper {
		max-width: 100%;
		overflow: hidden;
	}
</style>
